import axiosInstance from "./BaseService";
import ProgramData from "../models/ProgramData";

const fetchPrograms = async () => {
    try {
        const response = await axiosInstance.get<ProgramData[]>("/programs");
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch a program by ID
const fetchProgramById = async (programId: string) => {
    try {
        const response = await axiosInstance.get<ProgramData>(`/programs/${programId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch a program by programCode
const fetchProgramByCode = async (programCode: string) => {
    try {
        const response = await axiosInstance.get<ProgramData>(`/programs/code/${programCode}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch programs by program type
const fetchProgramsByType = async (programType: string) => {
    try {
        const response = await axiosInstance.get<ProgramData[]>(`/programs/search/programtype`, {
            params: { programTypeName: programType }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to create a new program
const createProgram = async (programData: ProgramData) => {
    try {
        const response = await axiosInstance.post<ProgramData>("/programs", programData);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to update an existing program
export async function updateProgram(
  id: string,
  data: Partial<ProgramData>
): Promise<any> {
    try {
        const response = await axiosInstance.put<ProgramData>(`/programs/${id}`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}
// Function to delete a program
const deleteProgram = async (programId: string) => {
    try {
        const response = await axiosInstance.delete(`/programs/${programId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch programs by pagination and orderby
const fetchProgramsByPagination = async (page: number, limit: number, orderBy: string = 'createdAt') => {
    try {
        const response = await axiosInstance.get<any>(`/programs/pagination`, {
            params: {
                page,
                limit,
                orderBy
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

//Function to search programs by title
const fetchProgramsBySearch = async (searchQuery: string) => {
    try {
        const response = await axiosInstance.get<ProgramData[]>(`/programs/search/` + searchQuery);
        return response.data;
    } catch (error) {
        throw error;
    }
}   

const fetchProgramsByGroup = async (groupId: string) => {
    try {
        const response = await axiosInstance.get<any>(`/programs/group/` + groupId);
        if (response.status !== 200) {
            throw new Error(`Error fetching programs for group ${groupId}`);
        }
        
        return response.data.programs || [];
    } catch (error) {
        throw error;
    }
}

// Function to fetch programs by group with pagination
const fetchProgramsByGroupPaginated = async (groupId: string, page: number = 1, limit: number = 6) => {
    try {
        const response = await axiosInstance.get<any>(`/programs/group/${groupId}/pagination`, {
            params: {
                page,
                limit
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}


// Exporting the functions to be used in other parts of the application
// Function to fetch program schedules by program ID
const fetchProgramSchedules = async (programId: string) => {
    try { 
        const response = await axiosInstance.get<any>(`/programschedules/program/${programId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch program members by program ID
const fetchProgramMembers = async (programId: string) => {
    try {
        const response = await axiosInstance.get<any>(`/programmembers/program/${programId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch programs by language code
const fetchProgramsByLanguage = async (languageCode: string) => {
    try {
        const response = await axiosInstance.get<any>(`/programs/language/${languageCode}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Unified search function with text, language, status, location filters, and pagination
const fetchProgramsUnified = async (page: number = 1, limit: number = 12, searchText: string = "", country: string = "", state: string = "", groupId: string = "", languageCode: string = "", status: string = "") => {
    try {
        const params: any = {
            page,
            limit
        };
        
        if (searchText) params.text = searchText;
        if (country) params.country = country;
        if (state) params.state = state;
        if (groupId) params.groupId = groupId;
        if (languageCode) params.language = languageCode;
        if (status) params.status = status;
        
        const response = await axiosInstance.get<any>(`/programs/search/unified`, {
            params
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch upcoming programs only
const fetchUpcomingPrograms = async (limit: number = 10) => {
    try {
        const response = await axiosInstance.get<any>(`/programs/search/upcoming`, {
            params: { limit }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to search schedules with filters
const searchSchedules = async (state: string = "", week: string = "this", page: number = 1, limit: number = 20) => {
    try {
        const response = await axiosInstance.get<any>(`/programschedules/search`, {
            params: {
                state,
                week,
                page,
                limit
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Function to fetch user's programs (plays or other types)
const fetchMyPrograms = async (page: number = 1, limit: number = 12, search: string = '', programType: string = '') => {
    try {
        const params: any = {
            page,
            limit
        };
        
        if (search) params.search = search;
        if (programType) params.programType = programType;
        
        const response = await axiosInstance.get<any>(`/programs/my-programs`, {
            params
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export {
    fetchPrograms,
    fetchProgramById,
    fetchProgramByCode,
    createProgram,    
    deleteProgram,
    fetchProgramsByPagination,
    fetchProgramsBySearch,
    fetchProgramsByType,
    fetchProgramsByGroup,
    fetchProgramsByGroupPaginated,
    fetchProgramSchedules,
    fetchProgramMembers,
    fetchProgramsByLanguage,
    fetchProgramsUnified,
    fetchUpcomingPrograms,
    searchSchedules,
    fetchMyPrograms
};